// @flow

import { $iq, Strophe } from 'strophe.js';

import logger from '../../app/logger';

import type { ConferenceInfo } from './roomInfo';

/**
 * Namespace served by mod_conference_info on the main VirtualHost.
 *
 * Deliberately not disco#info: mod_muc_hide_all answers disco on a room with
 * <forbidden/> unless the asker is already an occupant, which is exactly the
 * pre-join case. Widening disco would re-open room enumeration for everyone, so
 * the server offers this narrow query instead.
 */
const NS = 'meethour:conference-info';

/**
 * How long to wait for the server before treating a query as unanswered.
 */
const IQ_TIMEOUT = 5000;

/**
 * Reads the conference-info payload out of a stanza.
 *
 * @param {Object} stanza - IQ result or headline message.
 * @returns {?ConferenceInfo}
 */
function parseConferenceInfo(stanza: Object): ?ConferenceInfo {
    const element = stanza?.getElementsByTagName
        ? stanza.getElementsByTagName('conference-info')[0]
        : undefined;

    if (!element) {
        return null;
    }

    const readNumber = name => {
        const value = Number(element.getAttribute(name));

        return Number.isFinite(value) ? value : 0;
    };

    // Absent means the server does not know (no visitor-mode module), which is
    // not the same as false.
    const readOptionalBoolean = name => {
        const value = element.getAttribute(name);

        return value === null ? undefined : value === 'true';
    };
    const participantListVisible = element.getAttribute('participantListVisible') === 'true';
    const readPeople = (tagName, defaultRole) => Array.from(element.getElementsByTagName(tagName))
        .map(participant => {
            return {
                id: participant.getAttribute('id') || '',
                name: participant.getAttribute('name') || '',
                role: participant.getAttribute('role') || defaultRole
            };
        })
        .filter(participant => participant.id);

    // Absent, not empty, when the server did not authorize identities: the two
    // mean different things and only the server can tell them apart.
    const participantList = participantListVisible
        ? readPeople('participant', 'participant')
        : undefined;
    const waitingList = participantListVisible
        ? readPeople('waiting', 'waiting')
        : undefined;

    return {
        exists: element.getAttribute('exists') === 'true',
        participants: readNumber('participants'),
        moderators: readNumber('moderators'),
        waiting: readNumber('waiting'),
        hasHost: element.getAttribute('hasHost') === 'true',
        lobbyEnabled: element.getAttribute('lobbyEnabled') === 'true',
        membersOnly: element.getAttribute('membersOnly') === 'true',
        visitorsEnabled: readOptionalBoolean('visitorsEnabled'),
        participantList,
        waitingList
    };
}

/**
 * Resolves the Strophe-facing connection from an MHConnection.
 *
 * @param {Object} connection - The MHConnection opened at prejoin.
 * @returns {?Object}
 */
function getXmppConnection(connection: Object): ?Object {
    const xmppConnection = connection?.xmpp?.connection;

    return xmppConnection?.sendIQ ? xmppConnection : null;
}

/**
 * Returns the XMPP host the module is served from, which is the plain domain
 * rather than the MUC component.
 *
 * @param {Object} connection - The MHConnection.
 * @returns {?string}
 */
function getServiceHost(connection: Object): ?string {
    return connection?.options?.hosts?.domain
        ?? connection?.xmpp?.options?.hosts?.domain;
}

/**
 * Asks the server for the current state of a conference.
 *
 * Resolves null on any failure - no connection, a <forbidden/> because the JWT is
 * scoped elsewhere, or a timeout. Null means unknown and callers must fall back;
 * it never means the room is empty.
 *
 * @param {Object} connection - The MHConnection opened at prejoin.
 * @param {string} roomName - Backend-safe room name.
 * @returns {Promise<?ConferenceInfo>}
 */
export function queryConferenceInfo(
        connection: Object,
        roomName: string
): Promise<?ConferenceInfo> {
    const xmppConnection = getXmppConnection(connection);
    const host = getServiceHost(connection);

    if (!xmppConnection || !host || !roomName) {
        return Promise.resolve(null);
    }

    return new Promise(resolve => {
        xmppConnection.sendIQ(
            $iq({ type: 'get',
                to: host }).c('query', { xmlns: NS,
                room: roomName }),
            result => resolve(parseConferenceInfo(result)),
            error => {
                logger.debug(`conference-info query failed for ${roomName}: `
                    + `${Strophe.getText(error) || 'no answer'}`);
                resolve(null);
            },
            IQ_TIMEOUT
        );
    });
}

/**
 * Applies moderator-only room settings from the pre-join screen.
 *
 * Visitor mode is the setting that cannot wait for the moderator to be inside
 * the meeting: jicofo decides host-vs-visitor when it answers a client's
 * conference request, so an audience that arrives before the host is routed by
 * whatever the room said at that moment. The server verifies the moderator
 * claim on the JWT that authenticated this session, so nothing here is a
 * security decision.
 *
 * @param {Object} connection - The MHConnection opened at prejoin.
 * @param {string} roomName - Backend-safe room name.
 * @param {Object} attributes - Attributes for the configure element.
 * @returns {Promise<boolean>} Whether the server accepted the change.
 */
export function configureConferenceInfo(
        connection: Object,
        roomName: string,
        attributes: Object
): Promise<boolean> {
    const xmppConnection = getXmppConnection(connection);
    const host = getServiceHost(connection);

    if (!xmppConnection || !host || !roomName) {
        return Promise.resolve(false);
    }

    return new Promise(resolve => {
        xmppConnection.sendIQ(
            $iq({ type: 'set',
                to: host }).c('configure', {
                xmlns: NS,
                room: roomName,
                ...attributes
            }),
            () => resolve(true),
            error => {
                logger.warn(`conference-info configure failed for ${roomName}: `
                    + `${Strophe.getText(error) || 'no answer'}`);
                resolve(false);
            },
            IQ_TIMEOUT
        );
    });
}

/**
 * Tells the server whether this client is waiting for the meeting to start.
 *
 * This deployment holds a guest on the pre-join screen until a host arrives, so
 * they never send a MUC join and prosody has no occupant for them anywhere.
 * Without this the moderator's roster showed "nobody is waiting" while three
 * people sat looking at the waiting screen.
 *
 * Sent as a plain re-subscribe: the server keys a watcher by session, so this
 * updates the existing entry and installs no second stanza handler.
 *
 * @param {Object} connection - The MHConnection opened at prejoin.
 * @param {string} roomName - Backend-safe room name.
 * @param {boolean} waiting - Whether this client is waiting to be let in.
 * @param {string} [displayName] - Name to show the moderator.
 * @returns {void}
 */
export function declareWaiting(
        connection: Object,
        roomName: string,
        waiting: boolean,
        displayName?: string
) {
    const xmppConnection = getXmppConnection(connection);
    const host = getServiceHost(connection);

    if (!xmppConnection || !host || !roomName || !xmppConnection.connected) {
        return;
    }

    xmppConnection.sendIQ(
        $iq({ type: 'set',
            to: host }).c('subscribe', {
            xmlns: NS,
            room: roomName,
            waiting: String(Boolean(waiting)),
            name: displayName || ''
        }),
        () => { }, // eslint-disable-line no-empty-function
        error => logger.debug('conference-info waiting declaration failed: '
            + `${Strophe.getText(error) || 'no answer'}`),
        IQ_TIMEOUT
    );
}

/**
 * Subscribes to changes in a conference and calls back on each one.
 *
 * The server answers the subscription with the current state as well as
 * registering it, so the caller gets an immediate first value rather than having
 * to make a separate query that would race the first push.
 *
 * This is what removes polling from the waiting screen: the server tells a
 * waiting guest that the host arrived, instead of the guest asking repeatedly and
 * discovering it up to one interval late.
 *
 * @param {Object} connection - The MHConnection opened at prejoin.
 * @param {string} roomName - Backend-safe room name.
 * @param {Function} onUpdate - Called with each ConferenceInfo received.
 * @param {Object} [waiting] - This client's standing "I am waiting for the
 * meeting to start" declaration, carried on the subscription so it survives the
 * connection not being up when it was made, and every later reconnect.
 * @returns {Promise<?Function>} Unsubscribe function, or null when the
 * subscription could not be established and the caller should fall back.
 */
export async function subscribeConferenceInfo(
        connection: Object,
        roomName: string,
        onUpdate: Function,
        waiting?: Object
): Promise<?Function> {
    const xmppConnection = getXmppConnection(connection);
    const host = getServiceHost(connection);

    if (!xmppConnection || !host || !roomName) {
        return null;
    }

    // Registered before subscribing: the server replies to the subscription with
    // current state and may push again immediately, and a handler added after
    // that would miss it.
    const handlerRef = xmppConnection.addHandler(
        message => {
            const info = parseConferenceInfo(message);

            if (info) {
                onUpdate(info);
            }

            // Keep the handler installed for subsequent pushes.
            return true;
        },
        NS,
        'message'
    );

    const removeHandler = () => {
        try {
            xmppConnection.deleteHandler(handlerRef);
        } catch (e) {
            logger.debug(`could not remove conference-info handler: ${e.message}`);
        }
    };

    const established = await new Promise(resolve => {
        xmppConnection.sendIQ(
            $iq({ type: 'set',
                to: host }).c('subscribe', {
                xmlns: NS,
                room: roomName,
                waiting: String(Boolean(waiting?.waiting)),
                name: waiting?.displayName || ''
            }),
            result => {
                const info = parseConferenceInfo(result);

                if (info) {
                    onUpdate(info);
                }

                resolve(true);
            },
            error => {
                logger.debug(`conference-info subscribe failed for ${roomName}: `
                    + `${Strophe.getText(error) || 'no answer'}`);
                resolve(false);
            },
            IQ_TIMEOUT
        );
    });

    if (!established) {
        removeHandler();

        return null;
    }

    return () => {
        removeHandler();

        // Best effort: the server also drops subscriptions on resource-unbind, so
        // a failure here leaks nothing beyond the life of the session.
        //
        // Dispose runs on teardown paths — prejoin unmounting as the conference
        // takes over, a failed connection — where the connection is often already
        // gone. XmppConnection.sendIQ answers that case by calling errback('Not
        // connected') without checking that an errback was passed, so sending
        // this without one throws out of componentWillUnmount and aborts React's
        // unmount commit. Skip the send when there is nothing to send it on, and
        // never hand sendIQ an undefined callback.
        if (!xmppConnection.connected) {
            return;
        }

        xmppConnection.sendIQ(
            $iq({ type: 'set',
                to: host }).c('unsubscribe', { xmlns: NS,
                room: roomName }),
            () => { /* best effort, nothing to do on success */ },
            error => {
                logger.debug(`conference-info unsubscribe failed for ${roomName}: `
                    + `${Strophe.getText(error) || 'no answer'}`);
            },
            IQ_TIMEOUT
        );
    };
}
